import { useState, useRef, useEffect } from "react";
import { useLang } from "@/contexts/LangContext";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const generateSessionId = () => {
  return `chat-${Math.random().toString(36).slice(2)}-${Date.now()}`;
};

const AIChat = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]); // {role, content}
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !sessionId) {
      setSessionId(generateSessionId());
      setMessages([{ role: "assistant", content: t.chat.welcome }]);
    }
  }, [open, sessionId, t.chat.welcome]);

  useEffect(() => {
    // Reset session when language changes (welcome message will be regenerated)
    if (sessionId) {
      setMessages((prev) => {
        if (prev.length > 0 && prev[0].role === "assistant") {
          return [{ role: "assistant", content: t.chat.welcome }, ...prev.slice(1)];
        }
        return prev;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: trimmed }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed, language: lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).replace(/^ /, "");
          if (data === "[DONE]") continue;
          if (data.startsWith("[ERROR]")) {
            setMessages((m) => {
              const updated = [...m];
              updated[updated.length - 1] = {
                role: "assistant",
                content: "Sorry, I had trouble responding. Please try again.",
              };
              return updated;
            });
            continue;
          }
          setMessages((m) => {
            const updated = [...m];
            updated[updated.length - 1] = {
              role: "assistant",
              content: updated[updated.length - 1].content + data,
            };
            return updated;
          });
        }
      }
    } catch (err) {
      console.error(err);
      setMessages((m) => {
        const updated = [...m];
        updated[updated.length - 1] = {
          role: "assistant",
          content: "Sorry, connection error. Please try again.",
        };
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        className={`fixed ${
          lang === "ar" ? "left-6 bottom-6" : "right-6 bottom-24"
        } z-50 w-16 h-16 rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white shadow-2xl shadow-black/25 flex items-center justify-center transition-all duration-300 ${
          open ? "scale-90 rotate-90" : "hover:scale-110"
        }`}
        aria-label="AI Chat"
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </button>

      {open && (
        <div
          data-testid="ai-chat-window"
          className={`fixed ${
            lang === "ar" ? "left-6 bottom-28" : "right-6 bottom-44"
          } z-50 w-[calc(100vw-3rem)] max-w-[400px] h-[560px] bg-[#0A0A0A]/95 backdrop-blur-2xl border border-white/10 text-white flex flex-col shadow-2xl shadow-black/40`}
        >
          <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C62828] to-[#D4AF37] flex items-center justify-center font-heading text-lg">
              美
            </div>
            <div>
              <p className="font-heading text-lg leading-tight">{t.chat.title}</p>
              <p className="text-xs text-white/60">{t.chat.subtitle}</p>
            </div>
          </div>

          <div
            ref={scrollRef}
            data-testid="ai-chat-messages"
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#C62828] text-white"
                      : "bg-white/8 border border-white/10 text-white/90"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            {streaming && (
              <div className="flex items-center gap-2 text-white/50 text-xs">
                <Loader2 size={12} className="animate-spin" /> Mei is typing...
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 flex items-center gap-2">
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t.chat.placeholder}
              disabled={streaming}
              className="flex-1 bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/40 outline-none focus:border-[#D4AF37] transition-colors"
            />
            <button
              data-testid="chat-send-button"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed text-white w-12 h-12 flex items-center justify-center transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChat;
